<template>
    <!--
        Agencies list — mobile variant A (TAG portal refresh).
        Layout: top app bar → main scrollable body with title + sticky
        [search + status chips] + agency row list → bottom tab bar.
        Filter sheet slides up from the bottom over a dimmed scrim.

        Receives all backend data and filter state from the parent
        (Agencies/index.vue) and emits filter / status / search events
        back. The parent owns the source of truth — this component is
        a pure view + interaction layer for narrow viewports.
    -->
    <div :style="shellStyle">
        <!-- ===== Top app bar ===== -->
        <header :style="topBarStyle">
            <button type="button" :style="topBarBtnStyle" aria-label="Menu">
                <Icon name="menu" :size="18" />
            </button>
            <div :style="topBarBrandStyle">
                <span class="tag-logo-mark" :style="{ width: '22px', height: '22px', flex: '0 0 22px' }" />
                <span :style="topBarBrandTextStyle">TAG</span>
            </div>
            <Avatar name="Admin User" :size="36" color="oklch(0.62 0.17 25)" />
        </header>

        <!-- ===== Scrollable body ===== -->
        <div
            :style="bodyStyle"
            :class="{ 'is-dimmed': sheetOpen }"
        >
            <!-- Title block -->
            <div :style="titleWrapStyle">
                <div :style="titleRowStyle">
                    <h1 :style="titleH1Style">Agencies</h1>
                    <span class="tag-mono tag-tnum" :style="titleCountStyle">
                        {{ visibleCount === totalItems ? totalItems : `${visibleCount} / ${totalItems}` }}
                    </span>
                </div>
                <div :style="titleSubStyle">{{ subtitle }}</div>
            </div>

            <!-- Sticky search + status chips -->
            <div :style="stickyControlsStyle">
                <!-- Search field -->
                <div :style="searchWrapStyle">
                    <div :style="searchInputContainerStyle">
                        <span :style="searchIconStyle">
                            <Icon name="search" :size="16" />
                        </span>
                        <input
                            type="text"
                            placeholder="Search agencies"
                            :value="searchValue"
                            :style="searchInputStyle"
                            @input="$emit('search', $event.target.value)"
                            @focus="searchFocused = true"
                            @blur="searchFocused = false"
                        />
                        <button
                            v-if="searchValue"
                            type="button"
                            :style="clearBtnStyle"
                            @click="$emit('search', '')"
                            aria-label="Clear search"
                        >
                            <Icon name="xCircle" :size="16" />
                        </button>
                    </div>

                    <button
                        v-if="searchFocused || searchValue"
                        type="button"
                        :style="cancelBtnStyle"
                        @mousedown.prevent="onCancelSearch"
                    >Cancel</button>

                    <button
                        v-else
                        type="button"
                        :style="filterBtnStyle(activeFilterCount > 0)"
                        @click="openSheet"
                        aria-label="Filters"
                    >
                        <Icon name="sliders" :size="20" />
                        <span
                            v-if="activeFilterCount > 0"
                            :style="filterBadgeStyle"
                        >{{ activeFilterCount }}</span>
                    </button>
                </div>

                <!-- Status chips -->
                <div :style="statusChipsStyle">
                    <button
                        type="button"
                        class="btn btn--xs"
                        :style="chipStyle(selectedStatuses.length === 0)"
                        @click="$emit('status-click', null)"
                    >
                        All
                        <span class="tag-mono" :style="chipCountStyle(selectedStatuses.length === 0)">{{ totalItems }}</span>
                    </button>
                    <button
                        v-for="s in statusOptions"
                        :key="s.id"
                        type="button"
                        class="btn btn--xs"
                        :style="chipStyle(selectedStatuses.includes(s.id))"
                        @click="$emit('status-click', s.id)"
                    >
                        {{ s.label }}
                        <span
                            v-if="s.count != null"
                            class="tag-mono"
                            :style="chipCountStyle(selectedStatuses.includes(s.id))"
                        >{{ s.count }}</span>
                    </button>
                </div>
            </div>

            <!-- Agency row list / empty state -->
            <div :style="listWrapStyle">
                <div v-if="loading" :style="loadingStyle">Loading…</div>

                <template v-else-if="items.length">
                    <button
                        v-for="(item, i) in items"
                        :key="rowKey(item, i)"
                        type="button"
                        :style="rowStyle(i === items.length - 1)"
                        @click="onRowClick(item)"
                    >
                        <AgencyMark :name="nameOf(item)" :size="40" />
                        <div :style="rowBodyStyle">
                            <div :style="rowTitleStyle">
                                <span :style="rowTitleTextStyle">{{ nameOf(item) }}</span>
                            </div>
                            <div :style="rowMetaStyle">
                                <AgencyPill
                                    v-if="item.status"
                                    :status="String(item.status || '')"
                                />
                                <span v-if="item.service" :style="metaDotStyle" />
                                <span v-if="item.service">{{ item.service }}</span>
                                <template v-if="activeDealsOf(item) > 0">
                                    <span :style="metaDotStyle" />
                                    <span :style="rowDealsStyle">
                                        {{ activeDealsOf(item) }}
                                        {{ activeDealsOf(item) === 1 ? 'deal' : 'deals' }}
                                    </span>
                                </template>
                            </div>
                        </div>
                        <div :style="rowRightStyle">
                            <template v-if="ytdSalesOf(item) > 0">
                                <div :style="ytdValueStyle">
                                    ${{ ytdShort(ytdSalesOf(item)) }}
                                </div>
                                <div :style="ytdLabelStyle">YTD</div>
                            </template>
                            <Icon v-else name="chevRight" :size="12" />
                        </div>
                    </button>
                </template>

                <div v-else :style="emptyStateStyle">
                    <div :style="emptyIconStyle">
                        <Icon name="search" :size="18" />
                    </div>
                    <div :style="emptyTitleStyle">No agencies match</div>
                    <div :style="emptySubStyle">
                        <template v-if="searchValue">
                            Nothing for <b>"{{ searchValue }}"</b><template v-if="activeFilterCount > 0"> with these filters</template>.
                        </template>
                        <template v-else>
                            Try a different status or clear filters.
                        </template>
                    </div>
                    <div :style="emptyActionsStyle">
                        <button
                            v-if="searchValue"
                            type="button"
                            class="btn btn--sm"
                            @click="$emit('search', '')"
                        >Clear search</button>
                        <button
                            v-if="activeFilterCount > 0 || selectedStatuses.length > 0"
                            type="button"
                            class="btn btn--primary btn--sm"
                            @click="$emit('clear-filters')"
                        >Reset filters</button>
                    </div>
                </div>

                <div :style="{ height: '20px' }" />
            </div>
        </div>

        <!-- ===== Bottom sheet (filter) + scrim ===== -->
        <div
            v-if="sheetOpen"
            :style="scrimStyle"
            @click="closeSheet"
        />
        <div v-if="sheetOpen" :style="sheetStyle">
            <div :style="sheetGrabberWrapStyle" @click="closeSheet">
                <div :style="sheetGrabberStyle" />
            </div>
            <div :style="sheetHeaderStyle">
                <button
                    type="button"
                    :style="sheetResetBtnStyle(activeFilterCount > 0)"
                    :disabled="activeFilterCount === 0"
                    @click="$emit('clear-filters')"
                >Reset</button>
                <span :style="sheetTitleStyle">Filters</span>
                <button
                    type="button"
                    :style="sheetDoneBtnStyle"
                    @click="closeSheet"
                >Done</button>
            </div>

            <!-- Summary -->
            <div :style="summaryWrapStyle">
                <span :style="summaryIconStyle(activeFilterCount > 0)">
                    <Icon name="bolt" :size="14" />
                </span>
                <div :style="{ flex: '1 1 auto' }">
                    <div :style="summaryTitleStyle">
                        {{
                            activeFilterCount === 0
                                ? 'No filters active'
                                : `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} active`
                        }}
                    </div>
                    <div :style="summarySubStyle">
                        Matches <b :style="{ color: 'var(--text)' }">{{ visibleCount }}</b>
                        of {{ totalItems }} agencies
                    </div>
                </div>
            </div>

            <!-- Facet list -->
            <div :style="sheetBodyStyle">
                <div :style="facetCardStyle">
                    <FacetRow
                        v-for="(f, i) in facetDefs"
                        :key="f.id"
                        :facet="f"
                        :is-last="i === facetDefs.length - 1"
                        :is-open="openFacet === f.id"
                        :selected-options="readSelectedOptions(f)"
                        @toggle="toggleFacet(f.id)"
                        @select="onFacetSelect(f, $event)"
                    />
                </div>

                <div :style="budgetHeadingStyle">Budget</div>
                <div :style="facetCardStyle">
                    <button
                        v-for="(l, i, arr) in budgetOptions"
                        :key="l.value"
                        type="button"
                        :style="budgetRowStyle(i, budgetOptions.length)"
                        @click="$emit('update-filter', { key: 'budget_type', value: l.value })"
                    >
                        <span>{{ l.label }}</span>
                        <Icon
                            v-if="budgetType === l.value"
                            name="check"
                            :size="16"
                            :style="{ color: 'var(--tag-red)' }"
                        />
                    </button>
                </div>

                <div :style="{ height: '12px' }" />
            </div>

            <!-- CTA -->
            <div :style="sheetCtaWrapStyle">
                <button
                    type="button"
                    class="btn btn--primary"
                    :style="sheetCtaStyle"
                    @click="closeSheet"
                >
                    Show {{ visibleCount }}
                    {{ visibleCount === 1 ? 'agency' : 'agencies' }}
                </button>
            </div>
        </div>

        <!-- ===== Bottom tab bar ===== -->
        <nav :style="tabBarStyle">
            <a
                v-for="t in tabBarItems"
                :key="t.id"
                :href="t.href"
                :style="tabBarItemStyle(t.id === 'agencies')"
            >
                <Icon :name="t.icon" :size="20" />
                <span>{{ t.label }}</span>
            </a>
        </nav>
    </div>
</template>

<script>
import Icon from "./Icon.vue";
import Avatar from "./Avatar.vue";
import AgencyMark from "./AgencyMark.vue";
import AgencyPill from "./AgencyPill.vue";

// Inline facet-accordion row. Kept inside this file to avoid file
// proliferation — it's only used by the mobile filter sheet.
const FacetRow = {
    name: "FacetRow",
    components: { Icon },
    props: {
        facet: { type: Object, required: true },
        isLast: { type: Boolean, default: false },
        isOpen: { type: Boolean, default: false },
        selectedOptions: { type: Array, default: () => [] },
    },
    template: `
        <div :style="{ borderBottom: isLast && !isOpen ? 'none' : '1px solid var(--border-faint)' }">
            <button
                type="button"
                :style="headStyle"
                @click="$emit('toggle')"
            >
                <span :style="{ fontSize: '15px', fontWeight: 500, color: 'var(--text)' }">{{ facet.label }}</span>
                <span :style="rightStyle">
                    <span
                        v-if="selectedOptions.length > 0"
                        :style="selectedChipStyle"
                    >{{ selectedOptions[0] }}{{ selectedOptions.length > 1 ? ' +' + (selectedOptions.length - 1) : '' }}</span>
                    <span v-else :style="{ fontSize: '13px', color: 'var(--text-faint)' }">Any</span>
                    <span :style="chevStyle">
                        <Icon name="chevRight" :size="12" />
                    </span>
                </span>
            </button>
            <div v-if="isOpen" :style="optsWrapStyle">
                <button
                    v-for="opt in facet.options"
                    :key="opt.value"
                    type="button"
                    :style="optBtnStyle(isSelected(opt))"
                    @click="$emit('select', opt)"
                >
                    <Icon v-if="isSelected(opt)" name="check" :size="14" />
                    {{ opt.label }}
                </button>
            </div>
        </div>
    `,
    computed: {
        headStyle() {
            return {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "14px 16px",
                textAlign: "left",
                background: this.isOpen ? "var(--hover)" : "transparent",
                border: "none",
                cursor: "pointer",
            };
        },
        rightStyle() {
            return { display: "inline-flex", alignItems: "center", gap: "8px" };
        },
        selectedChipStyle() {
            return {
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                height: "24px",
                padding: "0 8px",
                borderRadius: "999px",
                background: "var(--tag-red-soft)",
                border: "1px solid var(--tag-red-border)",
                color: "var(--text)",
                fontSize: "12px",
                fontWeight: 600,
            };
        },
        chevStyle() {
            return {
                color: "var(--text-faint)",
                display: "inline-flex",
                transform: this.isOpen ? "rotate(90deg)" : "none",
                transition: "transform .15s",
            };
        },
        optsWrapStyle() {
            return {
                padding: "4px 14px 14px",
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                background: "var(--surface-2)",
            };
        },
    },
    methods: {
        isSelected(opt) {
            return this.selectedOptions.some(
                (s) => String(s) === String(opt.value) || String(s) === String(opt.label)
            );
        },
        optBtnStyle(on) {
            return {
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                height: "30px",
                padding: on ? "0 10px 0 8px" : "0 11px",
                borderRadius: "999px",
                cursor: "pointer",
                background: on ? "var(--text)" : "var(--surface)",
                color: on ? "var(--text-inv)" : "var(--text)",
                border: on ? "1px solid var(--text)" : "1px solid var(--border)",
                fontSize: "13px",
                fontWeight: on ? 500 : 400,
                letterSpacing: "-0.005em",
            };
        },
    },
};

export default {
    name: "AgenciesMobile",
    components: { Icon, Avatar, AgencyMark, AgencyPill, FacetRow },
    props: {
        items: { type: Array, required: true },
        loading: { type: Boolean, default: false },
        totalItems: { type: [Number, String], default: 0 },
        searchValue: { type: String, default: "" },
        selectedStatuses: { type: Array, default: () => [] },
        statusOptions: { type: Array, default: () => [] },
        filters: { type: Object, required: true },
        options: { type: Object, required: true },
        activeFilterCount: { type: Number, default: 0 },
    },
    data() {
        return {
            sheetOpen: false,
            searchFocused: false,
            openFacet: null,
        };
    },
    computed: {
        visibleCount() {
            return this.items.length;
        },
        subtitle() {
            const parts = [];
            if (this.activeFilterCount > 0 || this.selectedStatuses.length > 0 || this.searchValue) {
                parts.push("Filtered");
                if (this.selectedStatuses.length === 1) {
                    const s = this.statusOptions.find(
                        (o) => String(o.id) === String(this.selectedStatuses[0])
                    );
                    if (s) parts.push(s.label);
                }
                if (this.searchValue) parts.push(`"${this.searchValue}"`);
                return parts.join(" · ");
            }
            return `${this.totalItems} total`;
        },
        budgetType() {
            return this.filters.budget_type || "monthly";
        },
        budgetOptions() {
            return [
                { value: "monthly", label: "Monthly retainer fee" },
                { value: "project", label: "Project budget" },
                { value: "hourly", label: "Hourly rate" },
            ];
        },
        // Facet definitions are derived from the parent's backend-shaped
        // options. Each facet shows `selectedOptions[0] +N` in the head;
        // tapping reveals the options. `multi` means the user can pick
        // many; single-select facets clear before selecting.
        facetDefs() {
            return [
                {
                    id: "service",
                    label: "Service",
                    multi: true,
                    options: this.flattenOptions(this.options.service),
                    filterKey: "service",
                },
                {
                    id: "industry",
                    label: "Industry",
                    multi: true,
                    options: this.flattenOptions(this.options.industry),
                    filterKey: "industry",
                },
                {
                    id: "source",
                    label: "Source",
                    multi: false,
                    options: this.flattenOptions(this.options.source),
                    filterKey: "source",
                },
                {
                    id: "client_size",
                    label: "Client size",
                    multi: false,
                    options: this.flattenOptions(this.options.client_size),
                    filterKey: "client_size",
                },
                {
                    id: "international",
                    label: "International",
                    multi: false,
                    options: this.flattenOptions(this.options.international),
                    filterKey: "international",
                },
                {
                    id: "niche",
                    label: "Niche",
                    multi: false,
                    options: this.flattenOptions(this.options.niche),
                    filterKey: "niche",
                },
            ];
        },
        tabBarItems() {
            return [
                { id: "agencies", label: "Agencies", icon: "building",  href: "/agencies" },
                { id: "deals",    label: "Deals",    icon: "briefcase", href: "/deals" },
                { id: "sales",    label: "Sales",    icon: "trending",  href: "/sales" },
                { id: "invoices", label: "Invoices", icon: "receipt",   href: "/master-invoices" },
                { id: "me",       label: "More",     icon: "user",      href: "/admin" },
            ];
        },
        // ===== Styles =====
        shellStyle() {
            return {
                position: "relative",
                width: "100%",
                height: "100vh",
                background: "var(--bg)",
                fontFamily: "var(--font-sans)",
                color: "var(--text)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            };
        },
        topBarStyle() {
            return {
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px 12px",
                background: "var(--surface)",
                color: "var(--text)",
                borderBottom: "1px solid var(--border-faint)",
            };
        },
        topBarBtnStyle() {
            return {
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                border: "none",
                background: "var(--surface-2)",
                color: "var(--text-muted)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            };
        },
        topBarBrandStyle() {
            return { display: "flex", alignItems: "center", gap: "8px" };
        },
        topBarBrandTextStyle() {
            return {
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                fontSize: "15px",
            };
        },
        bodyStyle() {
            return {
                flex: "1 1 auto",
                overflow: "auto",
                position: "relative",
                filter: this.sheetOpen ? "brightness(0.92)" : "none",
                transition: "filter .2s",
            };
        },
        titleWrapStyle() {
            return { padding: "12px 16px 6px" };
        },
        titleRowStyle() {
            return { display: "flex", alignItems: "baseline", gap: "8px" };
        },
        titleH1Style() {
            return {
                margin: 0,
                font: "700 28px/1.1 var(--font-display)",
                letterSpacing: "-0.025em",
                color: "var(--text)",
            };
        },
        titleCountStyle() {
            return {
                fontSize: "14px",
                color: "var(--text-faint)",
                fontWeight: 500,
            };
        },
        titleSubStyle() {
            return {
                fontSize: "13px",
                color: "var(--text-muted)",
                marginTop: "3px",
            };
        },
        stickyControlsStyle() {
            return {
                position: "sticky",
                top: 0,
                zIndex: 5,
                background: "var(--bg)",
            };
        },
        searchWrapStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px 8px",
            };
        },
        searchInputContainerStyle() {
            return {
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                height: "40px",
                padding: "0 10px 0 12px",
                background: "var(--surface)",
                border: `1px solid ${this.searchFocused ? "var(--tag-red)" : "var(--border)"}`,
                boxShadow: this.searchFocused ? "var(--focus-ring)" : "none",
                borderRadius: "10px",
            };
        },
        searchIconStyle() {
            return { color: "var(--text-faint)", display: "inline-flex" };
        },
        searchInputStyle() {
            return {
                flex: "1 1 auto",
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: "15px",
                letterSpacing: "-0.01em",
                fontFamily: "var(--font-sans)",
                color: "var(--text)",
                padding: 0,
                minWidth: 0,
            };
        },
        clearBtnStyle() {
            return {
                background: "transparent",
                border: "none",
                padding: 0,
                color: "var(--text-faint)",
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
            };
        },
        cancelBtnStyle() {
            return {
                height: "40px",
                padding: "0 8px",
                border: "none",
                background: "transparent",
                color: "var(--tag-red)",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
            };
        },
        filterBadgeStyle() {
            return {
                position: "absolute",
                top: "-4px",
                right: "-4px",
                minWidth: "17px",
                height: "17px",
                padding: "0 4px",
                borderRadius: "999px",
                background: "var(--text)",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 700,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid var(--bg)",
            };
        },
        statusChipsStyle() {
            return {
                display: "flex",
                gap: "6px",
                padding: "4px 16px 12px",
                overflowX: "auto",
                scrollbarWidth: "none",
            };
        },
        listWrapStyle() {
            return { padding: 0 };
        },
        loadingStyle() {
            return {
                padding: "32px",
                textAlign: "center",
                color: "var(--text-muted)",
                fontSize: "13px",
            };
        },
        rowBodyStyle() {
            return { flex: "1 1 auto", minWidth: 0 };
        },
        rowTitleStyle() {
            return {
                fontSize: "15px",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--text)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
            };
        },
        rowTitleTextStyle() {
            return {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            };
        },
        rowMetaStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginTop: "4px",
                fontSize: "12px",
                color: "var(--text-muted)",
                flexWrap: "wrap",
            };
        },
        metaDotStyle() {
            return {
                width: "2px",
                height: "2px",
                borderRadius: "50%",
                background: "var(--border-strong)",
                display: "inline-block",
            };
        },
        rowDealsStyle() {
            return { color: "var(--text)", fontWeight: 500 };
        },
        rowRightStyle() {
            return { textAlign: "right", flex: "0 0 auto", color: "var(--text-faint)" };
        },
        ytdValueStyle() {
            return {
                fontFamily: "var(--font-mono)",
                fontVariantNumeric: "tabular-nums",
                fontSize: "14px",
                fontWeight: 600,
                color: "var(--text)",
            };
        },
        ytdLabelStyle() {
            return {
                fontSize: "10px",
                color: "var(--text-faint)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginTop: "1px",
                fontWeight: 600,
            };
        },
        emptyStateStyle() {
            return {
                margin: "24px 14px",
                padding: "32px 16px",
                borderRadius: "12px",
                background: "var(--surface)",
                border: "1px dashed var(--border)",
                textAlign: "center",
            };
        },
        emptyIconStyle() {
            return {
                width: "44px",
                height: "44px",
                margin: "0 auto 12px",
                borderRadius: "12px",
                background: "var(--surface-2)",
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            };
        },
        emptyTitleStyle() {
            return {
                fontSize: "15px",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                marginBottom: "4px",
            };
        },
        emptySubStyle() {
            return {
                fontSize: "13px",
                color: "var(--text-muted)",
                lineHeight: 1.4,
                marginBottom: "14px",
            };
        },
        emptyActionsStyle() {
            return { display: "inline-flex", gap: "8px" };
        },
        scrimStyle() {
            return {
                position: "absolute",
                inset: 0,
                background: "rgba(20,16,12,0.32)",
                zIndex: 20,
                cursor: "pointer",
            };
        },
        sheetStyle() {
            return {
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "640px",
                maxHeight: "85vh",
                zIndex: 30,
                background: "var(--bg)",
                borderRadius: "18px 18px 0 0",
                boxShadow: "0 -8px 30px rgba(20,16,12,0.18)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            };
        },
        sheetGrabberWrapStyle() {
            return {
                display: "flex",
                justifyContent: "center",
                padding: "8px 0 4px",
                cursor: "pointer",
            };
        },
        sheetGrabberStyle() {
            return {
                width: "36px",
                height: "4px",
                borderRadius: "999px",
                background: "var(--border-strong)",
            };
        },
        sheetHeaderStyle() {
            return {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 14px 12px",
            };
        },
        sheetTitleStyle() {
            return {
                fontSize: "15px",
                fontWeight: 700,
                letterSpacing: "-0.01em",
            };
        },
        sheetDoneBtnStyle() {
            return {
                background: "transparent",
                border: "none",
                color: "var(--tag-red)",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
            };
        },
        summaryWrapStyle() {
            return {
                margin: "0 14px 12px",
                padding: "12px",
                background: "var(--surface)",
                border: "1px solid var(--border-faint)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
            };
        },
        summaryTitleStyle() {
            return {
                fontSize: "13.5px",
                fontWeight: 600,
                letterSpacing: "-0.01em",
            };
        },
        summarySubStyle() {
            return {
                fontSize: "12px",
                color: "var(--text-muted)",
                marginTop: "1px",
            };
        },
        sheetBodyStyle() {
            return { flex: "1 1 auto", overflow: "auto" };
        },
        facetCardStyle() {
            return {
                background: "var(--surface)",
                margin: "0 14px",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid var(--border-faint)",
            };
        },
        budgetHeadingStyle() {
            return {
                margin: "16px 14px 0",
                fontSize: "11px",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 600,
                paddingBottom: "6px",
            };
        },
        sheetCtaWrapStyle() {
            return {
                padding: "10px 14px 14px",
                borderTop: "1px solid var(--border-faint)",
            };
        },
        sheetCtaStyle() {
            return {
                width: "100%",
                height: "46px",
                fontSize: "14px",
                fontWeight: 600,
            };
        },
        tabBarStyle() {
            return {
                flex: "0 0 auto",
                display: "flex",
                justifyContent: "space-around",
                padding: "6px 8px 14px",
                background: "var(--surface)",
                borderTop: "1px solid var(--border-faint)",
                boxShadow: "0 -1px 0 var(--border-faint)",
            };
        },
    },
    methods: {
        // ===== Style methods =====
        filterBtnStyle(active) {
            return {
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                border: active ? "none" : "1px solid var(--border)",
                background: active ? "var(--tag-red)" : "var(--surface)",
                color: active ? "#fff" : "var(--text)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                cursor: "pointer",
                flex: "0 0 40px",
            };
        },
        chipStyle(active) {
            return {
                flex: "0 0 auto",
                height: "28px",
                padding: "0 11px",
                gap: "6px",
                background: active ? "var(--text)" : "var(--surface)",
                color: active ? "var(--text-inv)" : "var(--text-muted)",
                borderColor: active ? "var(--text)" : "var(--border)",
                fontWeight: active ? 500 : 400,
                borderRadius: "999px",
                fontSize: "12.5px",
                cursor: "pointer",
            };
        },
        chipCountStyle(active) {
            return {
                fontSize: "10.5px",
                color: active
                    ? "color-mix(in oklch, var(--text-inv) 70%, transparent)"
                    : "var(--text-faint)",
            };
        },
        rowStyle(isLast) {
            return {
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
                padding: "12px 14px",
                borderBottom: isLast ? "none" : "1px solid var(--border-faint)",
                background: "var(--surface)",
                border: "none",
                borderRadius: 0,
                textAlign: "left",
                cursor: "pointer",
                font: "inherit",
                color: "inherit",
                margin: 0,
            };
        },
        sheetResetBtnStyle(enabled) {
            return {
                background: "transparent",
                border: "none",
                color: enabled ? "var(--text-muted)" : "var(--text-faint)",
                fontSize: "14px",
                fontWeight: 500,
                cursor: enabled ? "pointer" : "default",
            };
        },
        summaryIconStyle(active) {
            return {
                width: "28px",
                height: "28px",
                borderRadius: "8px",
                background: active ? "var(--tag-red-soft)" : "var(--surface-2)",
                color: active ? "var(--tag-red)" : "var(--text-muted)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            };
        },
        budgetRowStyle(i, total) {
            return {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "12px 16px",
                fontSize: "14px",
                borderBottom: i < total - 1 ? "1px solid var(--border-faint)" : "none",
                background: "transparent",
                border: "none",
                textAlign: "left",
                color: "var(--text)",
                cursor: "pointer",
            };
        },
        tabBarItemStyle(active) {
            return {
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2px",
                padding: "6px 10px",
                background: "transparent",
                color: active ? "var(--tag-red)" : "var(--text-faint)",
                fontSize: "10.5px",
                fontWeight: 600,
                letterSpacing: "0.01em",
                textDecoration: "none",
            };
        },
        // ===== Data helpers =====
        rowKey(item, i) {
            return (item && (item.id || (item.name && item.name.id))) || `row-${i}`;
        },
        nameOf(item) {
            const v = item.name;
            if (v && typeof v === "object") return v.name || "";
            return v || "";
        },
        activeDealsOf(item) {
            const v = Number(item.active_deals);
            return Number.isFinite(v) ? v : 0;
        },
        ytdSalesOf(item) {
            const raw = item.ytd_sales_amount ?? item.ytdSales ?? 0;
            const n = Number(String(raw).replace(/[,$\s]/g, ""));
            return Number.isFinite(n) ? n : 0;
        },
        ytdShort(value) {
            if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
            if (value >= 1_000) return (value / 1_000).toFixed(1) + "k";
            return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
        },
        // Flatten one level of treeselect-style options into plain
        // [{ value, label }] entries. Tolerates the existing backend
        // shape (`{ id, label, children? }`) without requiring schema
        // changes.
        flattenOptions(raw) {
            if (!Array.isArray(raw)) return [];
            const out = [];
            const walk = (list) => {
                list.forEach((o) => {
                    if (!o) return;
                    out.push({ value: o.id, label: o.label || o.name || String(o.id) });
                    if (Array.isArray(o.children)) walk(o.children);
                });
            };
            walk(raw);
            return out;
        },
        readSelectedOptions(facet) {
            const v = this.filters[facet.filterKey];
            if (facet.multi) {
                if (!Array.isArray(v)) return [];
                return v
                    .map((id) => facet.options.find((o) => String(o.value) === String(id)))
                    .filter(Boolean)
                    .map((o) => o.label);
            }
            if (v == null || v === "") return [];
            const found = facet.options.find((o) => String(o.value) === String(v));
            return found ? [found.label] : [];
        },
        // ===== UI events =====
        openSheet() {
            this.sheetOpen = true;
        },
        closeSheet() {
            this.sheetOpen = false;
            this.openFacet = null;
        },
        onCancelSearch() {
            this.$emit("search", "");
            this.searchFocused = false;
        },
        onRowClick(item) {
            const id = item.id || (item.name && item.name.id);
            if (!id) return;
            window.location.href = `/agencies/${id}/edit`;
        },
        toggleFacet(id) {
            this.openFacet = this.openFacet === id ? null : id;
        },
        onFacetSelect(facet, opt) {
            const current = this.filters[facet.filterKey];
            if (facet.multi) {
                const list = Array.isArray(current) ? [...current] : [];
                const idx = list.findIndex((v) => String(v) === String(opt.value));
                if (idx >= 0) list.splice(idx, 1);
                else list.push(opt.value);
                this.$emit("update-filter", { key: facet.filterKey, value: list });
            } else {
                const same = current != null && String(current) === String(opt.value);
                this.$emit("update-filter", {
                    key: facet.filterKey,
                    value: same ? null : opt.value,
                });
            }
        },
    },
};
</script>
